import { useCallback, useMemo, useState } from 'react';

import {
  getBodyAgeGroup,
  getBodyImage,
  getOrganImages,
  type OrganArtworkPart,
} from '#assets/images/gutXAxis';
import type { GutXAxisOrgan } from '@repo/types';
import { getAgeGroup } from '@repo/utils';

export type GutXAxisSystem = {
  organ: GutXAxisOrgan;
  parts: OrganArtworkPart[];
};

type UseGutXAxisImagesArgs = {
  organs: GutXAxisOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  waitForImages?: boolean;
};

/**
 * Resolves the drawings for a body and reports when they have all loaded, so the
 * organs appear together rather than one at a time.
 *
 * The count comes from the same parts that get rendered: an organ can own two
 * drawings or none, so any separately derived total would deadlock the paediatric
 * bodies, where fewer organs render than the kit has.
 */
export function useGutXAxisImages({
  organs,
  age,
  pregnant,
  sex,
  waitForImages = true,
}: UseGutXAxisImagesArgs) {
  const ageGroup = getAgeGroup(age);
  const bodyAgeGroup = getBodyAgeGroup({ age, pregnant });
  const body = getBodyImage(bodyAgeGroup);

  const systems = useMemo(() => {
    // The gut is recut around the organs beside it, so it needs the whole list.
    const drawnCodes = new Set(organs.map(organ => organ.lookup_code));

    return organs
      .map(organ => ({
        organ,
        parts: getOrganImages({
          ageGroup,
          sex,
          pregnant,
          drawnCodes,
          flagLevel: organ.flag_level,
          lookupCode: organ.lookup_code,
        }),
      }))
      .filter(system => system.parts.length > 0);
  }, [organs, ageGroup, sex, pregnant]);

  const partCount = systems.reduce(
    (total, system) => total + system.parts.length,
    0,
  );

  const [loaded, setLoaded] = useState<ReadonlySet<string>>(new Set());

  const mark = useCallback(
    (part: string) => {
      if (!waitForImages) return;
      setLoaded(prev => (prev.has(part) ? prev : new Set(prev).add(part)));
    },
    [waitForImages],
  );

  const imgProps = useCallback(
    (part: string) => ({
      // A cached drawing can finish before React attaches onLoad, so count it here.
      ref: (img: HTMLImageElement | null) => {
        if (img?.complete) mark(part);
      },
      onLoad: () => mark(part),
      // A drawing that fails still counts, or one bad asset holds the body forever.
      onError: () => mark(part),
    }),
    [mark],
  );

  return {
    body,
    bodyAgeGroup,
    systems,
    imgProps,
    ready: !waitForImages || loaded.size >= partCount + 1,
  };
}
